import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ShieldCheck, MessageCircle, Loader2, CheckCircle2, UserPlus, ExternalLink } from 'lucide-react';
import { KakoIdentity } from './KakoIdentity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * KakoAuthModal — Verificação de posse da conta KAKO via código no chat do app.
 * Reutilizável por qualquer fluxo: passe `purpose` e receba o token em `onVerified`.
 *
 * Props:
 *  - open, onOpenChange: controle do Dialog
 *  - purpose: finalidade da verificação (ex.: 'cadastro', 'votacao')
 *  - initialKakoId: pré-preenche o ID (opcional)
 *  - lockKakoId: impede edição do ID (opcional)
 *  - title: título customizado (opcional)
 *  - onVerified(token, kakoId): chamado após validação com sucesso
 */
export const KakoAuthModal = ({ open, onOpenChange, purpose = 'geral',
  initialKakoId = '', lockKakoId = false, title, onVerified, autoSend = false }) => {
  const [step, setStep] = useState('id'); // id | code | done
  const [kakoId, setKakoId] = useState(initialKakoId);
  const [nickname, setNickname] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [followUrl, setFollowUrl] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep('id'); setKakoId(initialKakoId); setNickname('');
      setChallengeId(''); setCode(''); setError(''); setResendIn(0); setExpiresIn(0);
      setFollowUrl('');
    }
    return () => clearInterval(timerRef.current);
  }, [open, initialKakoId]);

  // aberto já com o ID conferido (passo 1 do cadastro): pede o código sozinho
  useEffect(() => {
    if (open && autoSend && step === 'id' && kakoId && !busy && !challengeId) {
      requestCode();
    }
  }, [open, autoSend, kakoId]);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (step === 'code') {
      timerRef.current = setInterval(() => {
        setResendIn((s) => (s > 0 ? s - 1 : 0));
        setExpiresIn((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [step]);

  const requestCode = useCallback(async () => {
    if (!kakoId.trim()) { setError('Informe seu KAKO ID.'); return; }
    setBusy(true); setError(''); setFollowUrl('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kako-auth/request-code`, {
        kako_id: kakoId.trim(), purpose,
      });
      setChallengeId(res.data.challenge_id);
      setNickname(res.data.nickname || '');
      setResendIn(res.data.resend_in || 60);
      setExpiresIn(res.data.expires_in || 300);
      setCode('');
      setStep('code');
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.follow_required) {
        setFollowUrl(d.profile_url || '');
        setError(d.message || 'Siga a conta MoedasOnline no KAKO para receber o código.');
      } else {
        setError((typeof d === 'string' && d) || 'Não foi possível enviar o código. Tente novamente.');
      }
    } finally {
      setBusy(false);
    }
  }, [kakoId, purpose]);

  const verifyCode = useCallback(async () => {
    if (!/^\d{6}$/.test(code)) { setError('Digite os 6 dígitos do código.'); return; }
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kako-auth/verify-code`, {
        kako_id: kakoId.trim(), purpose, challenge_id: challengeId, code,
      });
      setStep('done');
      if (onVerified) onVerified(res.data.verification_token, res.data.kako_id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Código inválido ou expirado.');
    } finally {
      setBusy(false);
    }
  }, [code, kakoId, purpose, challengeId, onVerified]);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="kako-auth-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            {title || 'Verificação da conta KAKO'}
          </DialogTitle>
          <DialogDescription>
            Enviaremos um código de 6 dígitos no chat da sua conta no app KAKO Live.
          </DialogDescription>
        </DialogHeader>

        {step === 'id' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Seu KAKO ID</label>
              <Input
                data-testid="kako-auth-id-input"
                value={kakoId}
                disabled={lockKakoId}
                onChange={(e) => setKakoId(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex.: 10799542"
                inputMode="numeric"
                className="mt-1"
              />
            </div>
            <KakoIdentity kakoId={kakoId} testId="kako-auth-identity" />
            {followUrl ? (
              <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 text-sm"
                data-testid="kako-auth-follow-required">
                <p className="flex items-center gap-2 font-semibold text-orange-700">
                  <UserPlus className="h-4 w-4" /> Siga a MoedasOnline no KAKO
                </p>
                <p className="mt-1 text-gray-700">
                  O código é enviado no chat do app, e o KAKO só entrega mensagem de quem você segue.
                  Abra o perfil abaixo, toque em <b>Seguir</b> e volte aqui.
                </p>
                <a href={followUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="kako-auth-follow-link"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 font-bold text-white hover:bg-orange-600">
                  <ExternalLink className="h-4 w-4" /> Abrir perfil no KAKO
                </a>
              </div>
            ) : (error && <p className="text-sm text-red-600" data-testid="kako-auth-error">{error}</p>)}
            <Button
              data-testid="kako-auth-send-button"
              onClick={requestCode}
              disabled={busy}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              {followUrl ? 'Já estou seguindo — enviar código' : 'Enviar código no chat do app'}
            </Button>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
              <p>📩 Código enviado no chat do app KAKO{nickname ? <> para <b>{nickname}</b></> : ''} (ID <b>{kakoId}</b>).</p>
              <p className="mt-1 text-gray-600">Abra o app → Mensagens → conversa com <b>moedasonline.com</b>.</p>
            </div>
            <div>
              <label className="text-sm font-medium">Código de 6 dígitos</label>
              <Input
                data-testid="kako-auth-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="______"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
              />
              {expiresIn > 0 && (
                <p className="text-xs text-gray-500 mt-1">O código expira em {fmt(expiresIn)}</p>
              )}
              {expiresIn === 0 && (
                <p className="text-xs text-red-600 mt-1">Código expirado — solicite um novo.</p>
              )}
            </div>
            {followUrl ? (
              <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 text-sm"
                data-testid="kako-auth-follow-required">
                <p className="flex items-center gap-2 font-semibold text-orange-700">
                  <UserPlus className="h-4 w-4" /> Siga a MoedasOnline no KAKO
                </p>
                <p className="mt-1 text-gray-700">
                  O código é enviado no chat do app, e o KAKO só entrega mensagem de quem você segue.
                  Abra o perfil abaixo, toque em <b>Seguir</b> e volte aqui.
                </p>
                <a href={followUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="kako-auth-follow-link"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 font-bold text-white hover:bg-orange-600">
                  <ExternalLink className="h-4 w-4" /> Abrir perfil no KAKO
                </a>
              </div>
            ) : (error && <p className="text-sm text-red-600" data-testid="kako-auth-error">{error}</p>)}
            <Button
              data-testid="kako-auth-verify-button"
              onClick={verifyCode}
              disabled={busy || code.length !== 6}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Validar código
            </Button>
            <Button
              data-testid="kako-auth-resend-button"
              variant="outline"
              onClick={requestCode}
              disabled={busy || resendIn > 0}
              className="w-full"
            >
              {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar código'}
            </Button>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6 space-y-3" data-testid="kako-auth-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
            <p className="font-semibold text-lg">Conta verificada com sucesso!</p>
            <p className="text-sm text-gray-600">
              KAKO ID <b>{kakoId}</b> confirmado como seu.
            </p>
            <Button
              data-testid="kako-auth-close-button"
              onClick={() => onOpenChange(false)}
              className="bg-green-500 hover:bg-green-600"
            >
              Concluir
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KakoAuthModal;
